// ═══════════════════════════════════════════════════════════════════
// holoMaterial3D.js — SHIP 5: "THE MATERIAL LANGUAGE"
// ═══════════════════════════════════════════════════════════════════
// Rarity made PHYSICAL. In 2D a foil is a shader over a rectangle; on
// the Shatterfield a foil is an OBJECT — it is thicker, its edge is
// metal, and its face carries light that MOVES.
//
// Three layers, one file:
//   1. THICKNESS  — a foil card is a heavier slab. Reads at any angle,
//                   costs nothing, and is the only rarity cue that
//                   survives a card lying face-down in a stack.
//   2. EDGE       — the milled rim. Tier-colored metal with a slow rim
//                   pulse. This is the tell at TABLE distance, where
//                   the face is 40px tall and the shine is a smudge.
//   3. SHINE      — the FoilPlane: one extra plane above the art with a
//                   per-tier texture and a per-tier MOTION.
//
// ── FOIL LAW (terror-holofoil, carried into 3D verbatim) ───────────
// Every tier gets a BESPOKE MOTION SIGNATURE. Not a recolor. Nine
// tiers, nine different verbs:
//   silver   run + settle          gold      pour + twinkle
//   mythic   split + recombine     void      rotate (turbulence)
//   solrend  tear / flare / seal   aeternus  expand (heartbeat)
//   shadow   SUBTRACT (eclipse)    depths    writhe (caustics)
//   jynxi    seeded mischief
// If a tenth tier arrives, it gets a tenth verb, and it gets it HERE —
// this file is the only place a 3D foil recipe lives.
//
// ── THE THREE LAWS THIS FILE OBEYS ────────────────────────────────
// • PRNG LAW — every painted pixel comes from mulberry32 or a closed
//   form. Two clients render byte-identical foils, forever.
// • TEXTURE LAW — canvases are painted ONCE and cached module-level by
//   tier. Cards get a cheap `.clone()` so each owns its own UV
//   transform (offset/rotation/repeat live on the TEXTURE in three, so
//   a shared instance would make every card on the field animate as
//   one). This is the 3D twin of the SVG-filter-id law.
// • PERF LAW — no setState in useFrame. Every motion below writes
//   imperatively to a material/texture it owns.
//
// ── GAMEPLAY BEATS COSMETIC ───────────────────────────────────────
// When a card is glowing for a GAME reason (attacker gold, targetable
// red, revival green) the foil DIMS. A player must never lose a combat
// signal to a shiny. Card3D hands FoilPlane a live `dimRef`.
//
// Tier metadata (label / color) is NEVER duplicated here — it is read
// from PackArt's HOLO_TIER_INFO, the one registry.
// ═══════════════════════════════════════════════════════════════════
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import { HOLO_TIER_INFO, canonTier } from "./PackArt";
import { CARD } from "./fieldLayout3D";
// ⬡ SHIP 5.1 — THE CONTINUITY RULING (caP'n, July 25, 2026).
// The duel renders THE REAL FOIL. Not a 3D interpretation of it — the
// actual <HoloFoil> component the shop, the vault, the Codex and the
// Vault already use. A card must not change its face when it hits the
// table. One aura, many surfaces (IE_HOLO_DOCTRINE §1).
import HoloFoil, { getHoloVisual } from "./HoloFoil";
// ⬡ ONE URL BUILDER (ie-cdn-assets law). Also strips the Necro Seal —
// a Resurrected being's id carries an identity tag, and the hardcoded
// concatenation that used to live below asked the CDN for a file that
// has never existed, leaving every foil on a returned being shining
// on nothing.
import { cardUrl } from "./CDN";

// ═══════════════════════════════════════════════════════════════════
// FOIL FACE MODE — which layer draws the shine
// ═══════════════════════════════════════════════════════════════════
//   "surface" — THE DEFAULT AND THE LAW. The real <HoloFoil> DOM/SVG,
//               mounted on the card's face in 3D. Byte-identical to
//               every other surface in the game. A new 2D tier appears
//               in the duel automatically, with zero 3D work.
//   "native"  — the procedural 3D recipes further down this file. Kept
//               as an A/B and as a fallback if the DOM layer ever costs
//               too much on a weak device. NOT continuity-safe: these
//               are a different reading of each tier.
//   "off"     — slab + edge only. No face shine at all.
//
// Thickness and the milled edge run in ALL THREE modes — they are the
// part of rarity that 2D genuinely cannot express, so they are never
// in competition with the 2D foil.
//
// ── MODE FLIPPED TO "native" (July 24, flagged, not silent) ─────────
// Two sessions of live testing: "surface" mode renders NOTHING on
// caP'n's machine — not faint, absent — while non-transform <Html>
// (seat plates, badges) renders fine, which isolates the failure to
// drei's `transform` mode specifically. The blend-backdrop fix below
// (the art <img> inside the wrapper) is correct and stays, but a fix
// inside a layer that never paints is moot. The Continuity Ruling
// (5.1) is LAW and "surface" remains the target — but a law that
// draws zero pixels fails its own purpose, so the procedural WebGL
// recipes carry the duel until surface is debugged LIVE. Debug key
// for that session: with mode="surface", run in the browser console
//   document.querySelectorAll('.hf-root').length
// If it returns > 0 the DOM exists and it's a CSS transform/stacking
// problem; if 0, drei's <Html transform> never mounted. That one
// number halves the search space before a line is changed.
export const FOIL_FACE_MODE = "native";

// ── mulberry32 — THE seeded PRNG (PRNG LAW, copy verbatim) ──
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
// Deterministic 0..1 from two integers — the trickster's coin.
const hash2 = (a, b) => {
  let h = Math.imul((a | 0) ^ 0x9e3779b9, 0x85ebca6b);
  h = Math.imul(h ^ (b | 0) ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

// hex → {r,g,b} 0..1 (tier colors arrive as "#rrggbb" from the registry)
function rgbOf(hex) {
  const c = new THREE.Color(hex || "#ffffff");
  return { r: c.r, g: c.g, b: c.b };
}
const tierColor = (tier) => HOLO_TIER_INFO[canonTier(tier)]?.color || "#ffffff";

// ═══════════════════════════════════════════════════════════════════
// 1. THICKNESS — the slab
// ═══════════════════════════════════════════════════════════════════
// Multipliers on CARD.T. Ordered by TIER_ORDER rarity, so a stack of
// cards reads its own rarity ladder edge-on. A'eterni is double a
// common; nothing goes past that (the fan would start to gap).
//
// ⚠ STACK LAW: Card3D keeps a card's BOTTOM face where the caller put
// it and grows upward, so nothing below a thick card ever moves. When
// something sits ON a card (an escendant on its escling), the caller
// must ask holoThickness() for the lower card — SeatSide does.
const THICK_K = {
  holo_silver: 1.30,
  holo_gold: 1.42,
  holo_mythic: 1.58,
  holo_khao: 1.72,
  holo_solrend: 1.80,
  holo_jynxi: 1.68,
  holo_aeternus: 2.00,
  holo_depths: 1.86,
};
// Legacy keys resolve to their modern tier at every entry point below, so
// a pre-rename foil is physically identical to a post-rename one.
export const holoThickness = (tier) => CARD.T * (THICK_K[canonTier(tier)] || 1);

// ═══════════════════════════════════════════════════════════════════
// 2. EDGE — the milled rim
// ═══════════════════════════════════════════════════════════════════
// A plain card's edge is dead matte board and NEVER animates, so all
// of them share one material forever (one less material per card on a
// field that can hold thirty).
export const PLAIN_EDGE = new THREE.MeshStandardMaterial({
  color: "#1a1540", roughness: 0.7, metalness: 0.1,
});

// A foil edge is per-instance: it is animated, so sharing it would tie
// every card of that tier to one phase.
export function holoEdgeMaterial(tier) {
  // CONTINUITY: the edge takes the 2D recipe's OWN glow colour when the
  // recipe has one, so the extruded rim is the same light the card wears
  // in the shop. Registry colour is the fallback.
  const canon = canonTier(tier);
  const col = getHoloVisual(canon)?.glow || tierColor(canon);
  return new THREE.MeshStandardMaterial({
    color: col,
    emissive: new THREE.Color(col),
    emissiveIntensity: 0.35,
    // (the holo_shadow special-case that lived here — duller, less
    // mirror-like metal for the subtractive tier — went out with the
    // Shadow retirement. Restore it when the new Shadow ships.)
    metalness: 0.92,
    roughness: 0.18,
    toneMapped: false,
  });
}

// Rim helper — every motion calls this with its own numbers, so the
// rim pulse is part of the tier's signature, not a global heartbeat.
const rim = (edge, base, amp, speed, t, seed) => {
  if (!edge) return;
  edge.emissiveIntensity = base + Math.sin(t * speed + seed) * amp;
};

// ═══════════════════════════════════════════════════════════════════
// 3. SHINE — painted once, cached forever
// ═══════════════════════════════════════════════════════════════════
const FOIL_TEX = new Map();

function makeTex(key, w, h, painter) {
  if (FOIL_TEX.has(key)) return FOIL_TEX.get(key);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  painter(c.getContext("2d"), w, h);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  FOIL_TEX.set(key, t);
  return t;
}

// Per-card copy: shares the painted canvas, owns its own UV transform.
function cloneTex(base) {
  const t = base.clone();
  t.needsUpdate = true;
  t.center.set(0.5, 0.5);
  return t;
}

// Tileable pseudo-turbulence: sums of sines on INTEGER frequencies, so
// it wraps seamlessly in both axes no matter how the map is repeated
// or rotated. Deterministic — the phases come from mulberry32.
function fbm(w, h, seed, octaves = 5) {
  const R = mulberry32(seed);
  const layers = [];
  for (let o = 0; o < octaves; o++) {
    layers.push({
      fx: Math.max(1, Math.round(1 + o * 1.7 + R() * 2)),
      fy: Math.max(1, Math.round(1 + o * 1.5 + R() * 2)),
      px: R() * Math.PI * 2,
      py: R() * Math.PI * 2,
      a: 1 / (o + 1.35),
    });
  }
  const out = new Float32Array(w * h);
  let lo = 1e9, hi = -1e9;
  for (let y = 0; y < h; y++) {
    const v = y / h;
    for (let x = 0; x < w; x++) {
      const u = x / w;
      let n = 0;
      for (let i = 0; i < layers.length; i++) {
        const L = layers[i];
        n += L.a * Math.sin(2 * Math.PI * L.fx * u + L.px)
                 * Math.sin(2 * Math.PI * L.fy * v + L.py);
      }
      out[y * w + x] = n;
      if (n < lo) lo = n;
      if (n > hi) hi = n;
    }
  }
  const span = hi - lo || 1;
  for (let i = 0; i < out.length; i++) out[i] = (out[i] - lo) / span;
  return out;
}

// Write an RGBA field straight to the canvas (fast, exact, no paths).
function pixels(g, w, h, fn) {
  const img = g.createImageData(w, h);
  const d = img.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const p = fn(x / w, y / h, x, y);
      d[i] = p[0]; d[i + 1] = p[1]; d[i + 2] = p[2]; d[i + 3] = p[3];
    }
  }
  g.putImageData(img, 0, 0);
}

// ── silver · QUICKSILVER ──────────────────────────────────────────
// Horizontal bands of liquid metal. Motion runs DOWN the card, so the
// bands must vary in y and tile in y — closed-form sines do both.
const texSilver = () => makeTex("f|silver", 96, 256, (g, w, h) => {
  pixels(g, w, h, (u, v) => {
    const band =
      0.55 * Math.sin(2 * Math.PI * 3 * v) +
      0.30 * Math.sin(2 * Math.PI * 7 * v + 1.1) +
      0.15 * Math.sin(2 * Math.PI * 13 * v + 2.7);
    const a = Math.pow(Math.max(0, band * 0.5 + 0.5), 2.2);
    const tilt = 0.85 + 0.15 * Math.sin(2 * Math.PI * u);
    return [242, 246, 255, Math.round(a * tilt * 200)];
  });
});

// ── gold · MOLTEN ─────────────────────────────────────────────────
// A slower, fatter pour than silver, plus hard specks — the flecks
// that twinkle when the pour catches the lamp.
const texGold = () => makeTex("f|gold", 128, 256, (g, w, h) => {
  pixels(g, w, h, (u, v) => {
    const pour =
      0.60 * Math.sin(2 * Math.PI * 2 * v + 0.4) +
      0.28 * Math.sin(2 * Math.PI * 5 * v + 2.2) +
      0.12 * Math.sin(2 * Math.PI * 9 * v + 4.0);
    const a = Math.pow(Math.max(0, pour * 0.5 + 0.5), 1.9);
    return [255, 226, 150, Math.round(a * 190)];
  });
  const R = mulberry32(0xC01D);
  for (let i = 0; i < 150; i++) {
    const x = R() * w, y = R() * h, r = 0.6 + R() * 1.5;
    g.fillStyle = `rgba(255,250,220,${(0.35 + R() * 0.6).toFixed(3)})`;
    g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
  }
});

// ── mythic · PRISM ────────────────────────────────────────────────
// One white core bar with two satellite bars beside it: the split. The
// COLOR does the splitting (motion cycles hue out and back to white).
const texMythic = () => makeTex("f|mythic", 256, 64, (g, w, h) => {
  pixels(g, w, h, (u) => {
    const core = Math.exp(-Math.pow((u - 0.5) / 0.055, 2));
    const left = 0.55 * Math.exp(-Math.pow((u - 0.38) / 0.045, 2));
    const right = 0.55 * Math.exp(-Math.pow((u - 0.62) / 0.045, 2));
    const a = Math.min(1, core + left + right);
    return [255, 255, 255, Math.round(a * 225)];
  });
});

// ── void · THE MAELSTROM ──────────────────────────────────────────
// Turbulence with a throat at the center that EATS the light — alpha
// falls to nothing in the middle instead of peaking there.
const texKhao = () => makeTex("f|khao", 256, 256, (g, w, h) => {
  const n = fbm(w, h, 0x5EED, 5);
  pixels(g, w, h, (u, v, x, y) => {
    const d = Math.hypot(u - 0.5, v - 0.5) * 2;
    const throat = Math.min(1, Math.pow(Math.min(1, d / 0.62), 2.4));
    const smoke = Math.pow(n[y * w + x], 1.7);
    const a = smoke * throat;
    const heat = 0.5 + 0.5 * smoke;
    return [Math.round(180 * heat), Math.round(110 * heat), 255, Math.round(a * 205)];
  });
});

// ── solrend · THE REND ────────────────────────────────────────────
// A jagged vertical tear, white-hot at the seam and bleeding to red.
// Empty everywhere else — this tier is DARK most of the time.
const texSolrend = () => makeTex("f|solrend", 256, 358, (g, w, h) => {
  g.clearRect(0, 0, w, h);
  const R = mulberry32(0x50FA);
  const pts = [];
  for (let i = 0; i <= 14; i++) {
    pts.push({ x: w * 0.5 + (R() - 0.5) * w * 0.30, y: (i / 14) * h });
  }
  const ribbon = (width, style) => {
    g.beginPath();
    g.moveTo(pts[0].x - width, pts[0].y);
    pts.forEach((p) => g.lineTo(p.x - width, p.y));
    for (let i = pts.length - 1; i >= 0; i--) g.lineTo(pts[i].x + width, pts[i].y);
    g.closePath(); g.fillStyle = style; g.fill();
  };
  ribbon(26, "rgba(255,40,90,0.20)");
  ribbon(13, "rgba(255,120,60,0.48)");
  ribbon(5.5, "rgba(255,220,170,0.85)");
  ribbon(2, "rgba(255,255,245,1)");
});

// ── aeternus · HALO ───────────────────────────────────────────────
// Concentric rings. Motion expands them forever; nothing else in the
// nine is radially periodic, so the ring geometry alone identifies it.
const texAeternus = () => makeTex("f|aeternus", 256, 256, (g, w, h) => {
  pixels(g, w, h, (u, v) => {
    const d = Math.hypot(u - 0.5, v - 0.5) * 2;
    const ring = Math.pow(Math.max(0, Math.sin(d * Math.PI * 7.5)), 8);
    const falloff = Math.max(0, 1 - d * 0.85);
    const a = ring * falloff;
    return [245, 245, 255, Math.round(a * 235)];
  });
});

// ── shadow · ECLIPSE — RETIRED July 25, 2026 ──────────────────────
// THE INVERSION: black with alpha, drawn with normal blending so it
// REMOVES light instead of adding it; the corona is a band of spared
// alpha. Commented out with its recipe (below) so the build stays
// warning-clean, and kept verbatim because "the one foil that subtracts"
// is an idea worth thawing when the new Shadow is cut.
// const texShadow = () => makeTex("f|shadow", 256, 256, (g, w, h) => {
//   pixels(g, w, h, (u, v) => {
//     const d = Math.hypot(u - 0.5, v - 0.5) * 2;
//     let a = 0;
//     if (d < 0.52) a = 1;
//     else if (d < 0.60) a = 1 - (d - 0.52) / 0.08;   // umbra edge
//     if (d > 0.54 && d < 0.66) a *= 0.25;              // corona: spared
//     return [0, 0, 4, Math.round(Math.max(0, a) * 245)];
//   });
// });

// ── depths · CAUSTICS ─────────────────────────────────────────────
// Two interfering sine grids at a slight angle — the light-web on the
// floor of a pool. Tileable, so it can drift and wobble forever.
const texDepths = () => makeTex("f|depths", 256, 256, (g, w, h) => {
  pixels(g, w, h, (u, v) => {
    const a1 = Math.sin(2 * Math.PI * (4 * u + 3 * v));
    const a2 = Math.sin(2 * Math.PI * (3 * u - 4 * v) + 1.7);
    const a3 = Math.sin(2 * Math.PI * (7 * u + 1 * v) + 3.1);
    const web = Math.pow(Math.max(0, (a1 + a2 + 0.6 * a3) / 2.6), 3.2);
    return [190, 235, 255, Math.round(web * 230)];
  });
});

// ── jynxi · TRICKSTER ─────────────────────────────────────────────
// An honest-looking emerald sweep. The lie is in the motion.
const texJynxi = () => makeTex("f|jynxi", 256, 128, (g, w, h) => {
  pixels(g, w, h, (u, v) => {
    const bar = Math.exp(-Math.pow((u - 0.5) / 0.10, 2));
    const grin = 0.45 * Math.exp(-Math.pow((u - 0.5) / 0.24, 2))
                      * Math.pow(Math.max(0, Math.sin(v * Math.PI)), 3);
    const a = Math.min(1, bar + grin);
    return [180, 255, 205, Math.round(a * 215)];
  });
});

// ═══════════════════════════════════════════════════════════════════
// THE RECIPE BOOK — texture + blend + nine motions
// ═══════════════════════════════════════════════════════════════════
// motion(m, t, dt, seed):
//   m.mat  the FoilPlane material (opacity + color are yours)
//   m.map  its private texture (offset / repeat / rotation are yours)
//   m.edge the card's rim material, or null
//   t      elapsed seconds · dt frame delta · seed the card's own seed
//
// Color is written as a multiplier on the tier's own rgb so a flare can
// push past 1.0 — the materials are toneMapped:false, so >1 clips to
// white exactly like a real blowout.
const RECIPES = {
  holo_silver: {
    tex: texSilver, blend: THREE.AdditiveBlending, repeat: [1, 1.6],
    motion(m, t, dt, s) {
      // RUN + SETTLE: speed oscillates — the metal runs, slows, runs.
      const speed = 0.10 + 0.13 * Math.pow(Math.sin(t * 0.33 + s * 0.7), 2);
      m.map.offset.y -= speed * dt;
      m.mat.opacity = 0.40 + Math.sin(t * 0.9 + s) * 0.07;
      rim(m.edge, 0.40, 0.16, 1.1, t, s);
    },
  },
  holo_gold: {
    tex: texGold, blend: THREE.AdditiveBlending, repeat: [1, 1.35],
    motion(m, t, dt, s) {
      // POUR + TWINKLE: a steady pour, and flecks that catch the lamp.
      m.map.offset.y -= 0.075 * dt;
      const fleck = Math.max(0, Math.sin(t * 7.3 + s * 3.1) * Math.sin(t * 3.1 + s));
      const k = 1 + fleck * 0.75;
      m.mat.color.setRGB(this.rgb.r * k, this.rgb.g * k, this.rgb.b * k);
      m.mat.opacity = 0.46;
      rim(m.edge, 0.48, 0.18, 1.4, t, s);
    },
  },
  holo_mythic: {
    tex: texMythic, blend: THREE.AdditiveBlending, repeat: [1.15, 1],
    rotation: 0.72,
    motion(m, t, dt, s) {
      // SPLIT + RECOMBINE: a diagonal bar whose saturation swings from
      // white (recombined) out to full spectrum and back.
      m.map.offset.x += 0.17 * dt;
      const hue = (t * 0.11 + s * 0.017) % 1;
      const sat = Math.abs(Math.sin(t * 0.55 + s));
      m.mat.color.setHSL(hue, sat, 0.62);
      m.mat.opacity = 0.52;
      rim(m.edge, 0.5, 0.22, 1.7, t, s);
    },
  },
  holo_khao: {
    tex: texKhao, blend: THREE.AdditiveBlending, repeat: [1, 1],
    motion(m, t, dt, s) {
      // ROTATE: the maelstrom. The only tier that spins its own field —
      // CSS gradients could never sell this, a rotating map does.
      m.map.rotation += dt * 0.30;
      const pull = 1 + Math.sin(t * 0.6 + s) * 0.13;
      m.map.repeat.set(pull, pull);
      m.mat.opacity = 0.50 + Math.sin(t * 0.8 + s) * 0.11;
      rim(m.edge, 0.42, 0.20, 0.9, t, s);
    },
  },
  holo_solrend: {
    tex: texSolrend, blend: THREE.AdditiveBlending, repeat: [1.4, 1],
    motion(m, t, dt, s) {
      // TEAR / FLARE / SEAL. Not a loop — an EVENT, dark most of the
      // cycle. Seeded phase so two Rends on one field never sync.
      const P = 4.2;
      const p = (((t + (s % 420) / 100) % P) / P);
      let a = 0, k = 1, wide = 2.4;
      if (p >= 0.55 && p < 0.72) {          // opens
        const u = (p - 0.55) / 0.17;
        a = u * 0.85; k = 1 + u * 0.4; wide = 2.4 - u * 1.2;
      } else if (p >= 0.72 && p < 0.80) {   // FLARE
        const u = (p - 0.72) / 0.08;
        a = 0.85 + u * 0.15; k = 1.4 + u * 1.1; wide = 1.2 - u * 0.25;
      } else if (p >= 0.80) {               // seals
        const u = (p - 0.80) / 0.20, f = (1 - u) * (1 - u);
        a = f; k = 1 + 1.5 * f; wide = 0.95 + u * 1.45;
      }
      m.mat.opacity = a;
      m.map.repeat.x = wide;
      m.map.offset.x = Math.sin(t * 0.3 + s) * 0.04;
      m.mat.color.setRGB(this.rgb.r * k, this.rgb.g * k, this.rgb.b * k);
      rim(m.edge, 0.30 + a * 0.9, 0.05, 3.0, t, s);
    },
  },
  holo_aeternus: {
    tex: texAeternus, blend: THREE.AdditiveBlending, repeat: [1, 1],
    motion(m, t, dt, s) {
      // EXPAND + HEARTBEAT: rings ride outward on a double-thump. The
      // ring reset hides inside the fade at the end of each beat.
      const P = 1.9;
      const p = ((t / P) + s * 0.011) % 1;
      const r = 1.75 - 0.90 * p;
      m.map.repeat.set(r, r);
      const beat = Math.exp(-Math.pow((p - 0.06) / 0.05, 2))
                 + 0.70 * Math.exp(-Math.pow((p - 0.20) / 0.055, 2));
      const fade = p > 0.90 ? (1 - p) / 0.10 : 1;
      m.mat.opacity = (0.16 + beat * 0.55) * fade;
      const k = 1 + beat * 0.8;
      m.mat.color.setRGB(this.rgb.r * k, this.rgb.g * k, this.rgb.b * k);
      rim(m.edge, 0.35 + beat * 0.55, 0.03, 2.0, t, s);
    },
  },
  // ── SHADOW — RETIRED July 25, 2026 (caP'n's ruling) ──────────────
  // Read too close to Void/Khao (IE_HOLO_DOCTRINE §11). Khao kept the
  // artwork; Shadow leaves the shelf until a distinct one is cut. The
  // subtractive recipe is preserved verbatim — it is the only foil in
  // the book that REMOVES light, and that idea is worth keeping on ice.
  // texShadow() above is commented out alongside it.
  //
  // ⚠ DOCTRINE §5: while retired, holo_shadow copies render BASE.
  // Never alias them onto Khao to close the gap.
  // holo_shadow: {
  //   tex: texShadow, blend: THREE.NormalBlending, repeat: [1, 1], dark: true,
  //   motion(m, t, dt, s) {
  //     // SUBTRACT: the umbra crosses the face and takes the light with
  //     // it. At totality the rim blazes — that is the corona.
  //     const P = 6.5;
  //     const p = (((t + (s % 650) / 100) / P) % 1);
  //     m.map.offset.set(0.9 - 1.8 * p, 0.55 - 1.1 * p);
  //     const tot = Math.exp(-Math.pow((p - 0.5) / 0.16, 2));
  //     m.mat.opacity = 0.16 + tot * 0.64;
  //     rim(m.edge, 0.22 + tot * 1.35, 0.04, 2.4, t, s);
  //   },
  // },

  holo_depths: {
    tex: texDepths, blend: THREE.AdditiveBlending, repeat: [1.55, 1.55],
    motion(m, t, dt, s) {
      // WRITHE: drift plus a slow rotational wobble plus a breathing,
      // non-uniform repeat. One scroll reads as wallpaper; all three
      // together read as water.
      m.map.offset.x += 0.035 * dt;
      m.map.offset.y += 0.024 * dt;
      m.map.rotation = Math.sin(t * 0.23 + s) * 0.09;
      m.map.repeat.set(1.55 + Math.sin(t * 0.31 + s) * 0.07,
                       1.55 + Math.cos(t * 0.27 + s) * 0.07);
      m.mat.opacity = 0.34 + Math.sin(t * 0.6 + s) * 0.12;
      rim(m.edge, 0.38, 0.20, 0.8, t, s);
    },
  },
  holo_jynxi: {
    tex: texJynxi, blend: THREE.AdditiveBlending, repeat: [1.2, 1],
    motion(m, t, dt, s) {
      // MISCHIEF: an honest sweep that lies on a schedule only the card
      // knows. Seeded by (card, cycle) — deterministic for every client,
      // unpredictable to every player. Friend or faux.
      const CY = 2.7;
      const tt = t + (s % 270) / 100;
      const idx = Math.floor(tt / CY);
      const local = (tt % CY) / CY;
      const coin = hash2(s, idx);
      const dir = coin < 0.34 ? -1 : 1;
      m.map.offset.x += dir * 0.15 * dt;
      let a = 0.40, k = 1;
      if (coin >= 0.34 && coin < 0.62 && local > 0.50 && local < 0.565) {
        a = 0.95; k = 2.1;                 // FLASH — it grins
      } else if (coin >= 0.62 && coin < 0.80 && local > 0.45 && local < 0.62) {
        a = 0;                             // VANISH — it was never there
      }
      m.mat.opacity = a;
      m.mat.color.setRGB(this.rgb.r * k, this.rgb.g * k, this.rgb.b * k);
      rim(m.edge, 0.36 + (k > 1 ? 0.7 : 0), 0.14, 1.9, t, s);
    },
  },
};

// Bind each recipe its own rgb once (motions read `this.rgb`).
Object.keys(RECIPES).forEach((k) => { RECIPES[k].rgb = rgbOf(tierColor(k)); });

export const hasFoil3D = (tier) => !!(tier && RECIPES[canonTier(tier)]);

// ═══════════════════════════════════════════════════════════════════
// FoilSurface — THE REAL FOIL, ON THE CARD, IN THE WORLD
// ═══════════════════════════════════════════════════════════════════
// `<Html transform>` mounts genuine DOM into the scene graph, so the
// SVG feTurbulence, the SMIL, the conic gradients, the blend modes and
// every keyframe of the shipped 2D recipe render EXACTLY as they do in
// the shop. Not approximated. The same component.
//
// ── SIZING ────────────────────────────────────────────────────────
// In transform mode the DOM lives in world coordinates: 1 CSS pixel =
// 1 world unit at scale 1. So a 200×280 div (the card's own 5:7) scaled
// by CARD.W/200 lands exactly on the card's footprint — 1.0 × 1.4.
// Change PX_W alone and the fit follows.
//
// ⚠ THE 4.1 TRAP: `transform` + `sprite` together silently drew nothing
// on a flat-rotated node. `sprite` is deliberately NOT used here — the
// foil must lie IN the card's plane, not billboard at the camera, so it
// gets an explicit rotation instead. If a foil ever renders blank, this
// comment is the first place to look.
//
// ⚠ OCCLUSION: DOM composites over the canvas, so a foil can bleed
// through a card physically in front of it. `occlude` fixes it but
// costs a render pass per instance. Left OFF — the table is viewed from
// above where overlap is rare. Revisit if the hand fan looks wrong.
const PX_W = 200;
const PX_H = Math.round(PX_W * (CARD.H / CARD.W)); // 280 — the card's own ratio

export function FoilSurface({ tier, t: thick = CARD.T, dimRef = null, cardId = null }) {
  const wrapRef = useRef();
  const cur = useRef(1);
  const canon = canonTier(tier);

  useFrame(() => {
    const el = wrapRef.current;
    if (!el) return;
    // GAMEPLAY BEATS COSMETIC — the one 3D-side law that still applies
    // to the 2D foil. A targeting pulse must always win the eye, so the
    // whole foil layer fades rather than fighting it.
    const target = dimRef && dimRef.current ? 0.34 : 1;
    cur.current += (target - cur.current) * 0.15;
    el.style.opacity = cur.current.toFixed(3);
  });

  return (
    <Html
      transform
      position={[0, thick / 2 + 0.0026, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
      scale={CARD.W / PX_W}
      zIndexRange={[16, 8]}
      style={{ pointerEvents: "none" }}
    >
      <div ref={wrapRef} style={{
        position: "relative", width: PX_W, height: PX_H,
        borderRadius: 9, overflow: "hidden",
        pointerEvents: "none", willChange: "opacity",
        // ── THE BLEND-BACKDROP FIX (July 24) ──────────────────────────
        // isolation makes THIS div the blend group, so HoloFoil's
        // dodge/soft-light/multiply passes composite against the art
        // <img> below instead of escaping to the page backdrop. See the
        // long note under "why the foils were invisible" just above.
        isolation: "isolate",
      }}>
        {/* ── WHY THE FOILS WERE INVISIBLE (the July 24 find) ──────────
            <Html> is DOM. The card art it floats over is WebGL — pixels
            inside the <canvas>, a DIFFERENT ELEMENT. CSS mix-blend-mode
            can only blend against ancestors/siblings in its own stacking
            context; it can NEVER reach into the canvas. So every
            color-dodge / soft-light / multiply layer inside HoloFoil was
            compositing against this wrapper's TRANSPARENT backdrop —
            dodge over nothing is nothing. The recipes ran perfectly and
            painted almost no pixels. The fix: give the foil its art back.
            The same CDN webp the mesh face carries, mounted UNDER the
            HoloFoil inside this isolated group, sitting pixel-exact on
            the card's footprint (the wrapper IS the footprint). The DOM
            art visually replaces the mesh art beneath it — same image,
            same rect — and the foil finally has a card to shine on.
            No cardId (shouldn't happen — foils imply a real card) or a
            failed load → transparent fallback: faint foil, never broken. */}
        {cardId && (
          <img
            src={cardUrl(cardId)}
            alt=""
            draggable={false}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover", display: "block", pointerEvents: "none",
            }}
          />
        )}
        {/* The shop's component. Unmodified. That is the entire point. */}
        <HoloFoil tier={canon} radius={9} />
      </div>
    </Html>
  );
}

// ═══════════════════════════════════════════════════════════════════
// useFoilRim — the milled edge, driven by the 2D recipe's OWN numbers
// ═══════════════════════════════════════════════════════════════════
// In surface mode the face is the 2D foil, so the edge must not follow
// some separate 3D invention either. It pulses at the tier's own
// `pulseMs` in the tier's own `glow` colour, both read straight out of
// HOLO_VISUALS. The rim is the 2D recipe, extruded.
export function useFoilRim(tier, edgeMat, seed = 0) {
  const v = useMemo(() => getHoloVisual(canonTier(tier)), [tier]);
  useFrame((state) => {
    if (!edgeMat || !v) return;
    const period = Math.max(0.5, v.pulseMs || 4.5); // recipe seconds
    const phase = (state.clock.elapsedTime / period) * Math.PI * 2 + (seed % 100) * 0.06;
    edgeMat.emissiveIntensity = 0.42 + Math.sin(phase) * 0.2;
  });
}

// ═══════════════════════════════════════════════════════════════════
// FoilPlane — the procedural 3D shine ("native" mode only)
// ═══════════════════════════════════════════════════════════════════
// Rides above the art at the card's own thickness, so it inherits every
// flight flip, hit-shake and idle breathe for free. Never clickable,
// never depth-writing, never in anyone's way.
//
// `dimRef` is a live ref owned by Card3D: 1 while a GAMEPLAY light is
// on the card, 0 otherwise. Gameplay beats cosmetic — the foil drops to
// a third so a targeting pulse always wins the eye.
export function FoilPlane({ tier, seed = 0, t: thick = CARD.T, dimRef = null,
                            edge = null }) {
  const R = RECIPES[canonTier(tier)];
  const matRef = useRef();
  const cur = useRef(1);

  const map = useMemo(() => {
    if (!R) return null;
    const m = cloneTex(R.tex());
    if (R.repeat) m.repeat.set(R.repeat[0], R.repeat[1]);
    if (R.rotation) m.rotation = R.rotation;
    return m;
  }, [tier]); // eslint-disable-line react-hooks/exhaustive-deps

  useFrame((state, dt) => {
    const mat = matRef.current;
    if (!R || !mat || !map) return;
    const d = Math.min(dt, 0.05); // tab-return guard: never a giant step
    // ONE motion call drives BOTH the face and the rim, so an edge can
    // never fall out of phase with the shine it belongs to.
    R.motion({ mat, map, edge }, state.clock.elapsedTime, d, seed);
    // Drift guard — a duel left open for hours would otherwise push the
    // UV offset into float mush. Wrapping is invisible (RepeatWrapping).
    if (map.offset.x > 1 || map.offset.x < -1) map.offset.x %= 1;
    if (map.offset.y > 1 || map.offset.y < -1) map.offset.y %= 1;
    // Gameplay dim — lerped so a pulse ending doesn't snap the foil back.
    const target = dimRef && dimRef.current ? 0.34 : 1;
    cur.current += (target - cur.current) * 0.15;
    mat.opacity *= cur.current;
  });

  // The clone is this card's own; the module-cached base it copied from
  // is NOT ours to touch (disposing that would blank every card of the
  // tier). Cards leave the field constantly — clean up after ourselves.
  useEffect(() => () => { map?.dispose?.(); }, [map]);

  if (!R || !map) return null;
  return (
    <mesh position={[0, thick / 2 + 0.0026, 0]} rotation={[-Math.PI / 2, 0, 0]}
          raycast={() => null}>
      <planeGeometry args={[CARD.W * 0.985, CARD.H * 0.985]} />
      <meshBasicMaterial
        ref={matRef}
        map={map}
        color={R.dark ? "#000006" : tierColor(tier)}
        transparent
        opacity={0.4}
        blending={R.blend}
        depthWrite={false}
        toneMapped={false}
      />
    </mesh>
  );
}
